import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Plus, Tv, Film, Users, Share2, Check, BarChart3, Sparkles, Pencil, X, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SortableSeriesList } from '../components/SortableSeriesList';
import { SearchSeriesDialog } from '../components/SearchSeriesDialog';
import { SeriesDetailsDialog } from '../components/SeriesDetailsDialog';
import { MarkWatchedDialog } from '../components/MarkWatchedDialog';
import { RankNewSeriesDialog } from '../components/RankNewSeriesDialog';
import { JointListCombinedView } from '../components/JointListCombinedView';
import { AppHeader } from '../components/AppHeader';
import { getTMDBDetails } from '../services/mediaService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WatchlistDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mediaType, setMediaType] = useState('tv'); // 'tv' or 'movie'
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesDetails, setSeriesDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isMarkWatchedOpen, setIsMarkWatchedOpen] = useState(false);
  const [markWatchedSeries, setMarkWatchedSeries] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [rankingStep, setRankingStep] = useState(false);
  const [newRankings, setNewRankings] = useState([]);
  const [isRankNewOpen, setIsRankNewOpen] = useState(false);
  const [newlyAddedSeries, setNewlyAddedSeries] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rankingView, setRankingView] = useState('combined'); // 'combined' or user_id
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  // First watch and comment state for Mark Watched flow
  const [isFirstWatch, setIsFirstWatch] = useState(true);
  const [isBigScreen, setIsBigScreen] = useState(false);
  const [watchComment, setWatchComment] = useState('');
  const [watchDate, setWatchDate] = useState('');
  const [primaryGenre, setPrimaryGenre] = useState(null);
  // 1v1 comparison ranking state
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);
  const [savingWatched, setSavingWatched] = useState(false);
  
  // Invite friends state
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [invitingFriend, setInvitingFriend] = useState(null);
  
  const isJointList = watchlist?.list_type === 'joint';
  const memberCount = 1 + (watchlist?.shared_with?.length || 0);
  
  // Get all members for ranking toggle (owner + shared users)
  const allMembers = React.useMemo(() => {
    if (!watchlist || !isJointList) return [];
    const members = [{ 
      user_id: watchlist.user_id, 
      name: watchlist.owner_name || 'Owner',
      picture: watchlist.owner_picture
    }];
    if (watchlist.shared_users) {
      members.push(...watchlist.shared_users.map(u => ({ 
        user_id: u.user_id, 
        name: u.name,
        picture: u.picture
      })));
    }
    return members;
  }, [watchlist, isJointList]);
  
  // Check if current user has an empty list (new joiner)
  const currentUserHasEmptyList = isJointList && watchlist?.current_user_has_empty_list;
  
  // Get series list based on current ranking view
  const displayedSeries = React.useMemo(() => {
    if (!watchlist?.series) return [];
    if (!isJointList || rankingView === 'combined') {
      return watchlist.series;
    }
    // Show individual user's ranking - ONLY their items
    const userRanking = watchlist.user_rankings?.find(ur => ur.user_id === rankingView);
    if (!userRanking?.series_order?.length) {
      return []; // Empty list if user hasn't added any shows
    }
    // Map series by tmdb_id for lookup
    const seriesMap = {};
    watchlist.series.forEach(s => { seriesMap[s.tmdb_id] = s; });
    // Only show items in this user's series_order
    const ordered = userRanking.series_order
      .filter(tid => seriesMap[tid])
      .map((tid, idx) => ({ ...seriesMap[tid], rank: idx + 1 }));
    return ordered;
  }, [watchlist, isJointList, rankingView]);
  
  // Get current user's personal series list for joint lists
  const getUserPersonalSeries = () => {
    if (!watchlist || !isJointList || !user?.user_id) return [];
    const userRanking = watchlist.user_rankings?.find(ur => ur.user_id === user.user_id);
    if (!userRanking?.series_order?.length) return [];
    
    // Map series by tmdb_id for lookup
    const seriesMap = {};
    watchlist.series?.forEach(s => { seriesMap[s.tmdb_id] = s; });
    
    // Return only items in this user's ranking, in their order
    return userRanking.series_order
      .filter(tid => seriesMap[tid])
      .map((tid, idx) => ({ ...seriesMap[tid], rank: idx + 1 }));
  };
  
  const handleCopyShareLink = async () => {
    if (!watchlist?.share_code) return;
    const shareUrl = `${window.location.origin}/join/${watchlist.share_code}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleStartEditTitle = () => {
    setEditTitle(watchlist.title);
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditTitle('');
  };

  const handleSaveTitle = async () => {
    if (!editTitle.trim() || editTitle === watchlist.title) {
      setIsEditingTitle(false);
      return;
    }
    try {
      const response = await axios.put(`${API}/watchlists/${id}`, { title: editTitle.trim() }, { withCredentials: true });
      setWatchlist(response.data);
      setIsEditingTitle(false);
      toast.success('List renamed!');
    } catch (error) {
      toast.error('Failed to rename list');
    }
  };
  
  // Fetch friends for invite dialog
  const fetchFriends = async () => {
    setLoadingFriends(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/messages/friends`, { withCredentials: true, headers });
      // Filter out users already in the list
      const existingMembers = [watchlist?.user_id, ...(watchlist?.shared_with || [])];
      const availableFriends = (response.data.friends || []).filter(
        f => !existingMembers.includes(f.user_id)
      );
      setFriends(availableFriends);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
      setFriends([]);
    } finally {
      setLoadingFriends(false);
    }
  };
  
  // Handle inviting a friend to the list
  const handleInviteFriend = async (friendId) => {
    setInvitingFriend(friendId);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/messages/invite-to-list?recipient_id=${friendId}&list_id=${id}`,
        {},
        { withCredentials: true, headers }
      );
      toast.success('Invitation sent!');
      // Remove from available friends list
      setFriends(prev => prev.filter(f => f.user_id !== friendId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInvitingFriend(null);
    }
  };
  
  // Open invite friends dialog
  const handleOpenInviteFriends = () => {
    setShowInviteFriends(true);
    fetchFriends();
  };
  
  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        const response = await axios.get(`${API}/watchlists/${id}`, { withCredentials: true });
        setWatchlist(response.data);
      } catch (error) {
        toast.error('Failed to load watchlist');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchWatchlist();
  }, [id, navigate]);
  
  // Clear results when media type changes
  useEffect(() => {
    setSearchResults([]);
  }, [mediaType]);
  
  const handleSearch = async (searchMediaType) => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      // Use passed media type or fall back to state
      const typeToSearch = searchMediaType || mediaType;
      const response = await axios.get(`${API}/tmdb/search`, { 
        params: { query: searchQuery, media_type: typeToSearch }, 
        withCredentials: true 
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };
  
  const handleAddSeries = async (series) => {
    try {
      const itemMediaType = series.media_type || mediaType;
      
      // Use canonical media API for details
      let details;
      try {
        details = await getTMDBDetails(series.tmdb_id, itemMediaType);
      } catch {
        // Fallback to old API
        const endpoint = itemMediaType === 'movie' ? `/tmdb/movie/${series.tmdb_id}` : `/tmdb/tv/${series.tmdb_id}`;
        const detailsRes = await axios.get(`${API}${endpoint}`, { withCredentials: true });
        details = detailsRes.data;
      }
      
      const newSeriesData = {
        tmdb_id: series.tmdb_id, 
        title: series.title, 
        poster_path: series.poster_path,
        first_air_date: series.first_air_date, 
        genres: details.genres || [], 
        vote_average: series.vote_average,
        media_type: itemMediaType
      };
      
      const response = await axios.post(`${API}/watchlists/${id}/series`, newSeriesData, { withCredentials: true });
      
      setWatchlist(response.data);
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      
      // For joint lists, check user's personal ranking length
      // For personal lists, check global series length
      const isJoint = response.data.list_type === 'joint';
      let userSeriesCount = 0;
      
      if (isJoint && user?.user_id) {
        const userRanking = response.data.user_rankings?.find(ur => ur.user_id === user.user_id);
        userSeriesCount = userRanking?.series_order?.length || 0;
      } else {
        userSeriesCount = response.data.series?.length || 0;
      }
      
      // If there are other shows in the user's list, prompt to rank
      if (userSeriesCount > 1) {
        setNewlyAddedSeries(newSeriesData);
        setIsRankNewOpen(true);
      } else {
        toast.success(`Added "${series.title}"!`);
      }
    } catch (error) {
      toast.error(error.response?.status === 400 ? 'Already in list' : 'Failed to add');
    }
  };
  
  // Quick Add - adds to top or bottom without ranking dialog
  const handleQuickAdd = async (series, position) => {
    try {
      const itemMediaType = series.media_type || mediaType;
      
      // Use canonical media API for details
      let details;
      try {
        details = await getTMDBDetails(series.tmdb_id, itemMediaType);
      } catch {
        // Fallback to old API
        const endpoint = itemMediaType === 'movie' ? `/tmdb/movie/${series.tmdb_id}` : `/tmdb/tv/${series.tmdb_id}`;
        const detailsRes = await axios.get(`${API}${endpoint}`, { withCredentials: true });
        details = detailsRes.data;
      }
      
      const newSeriesData = {
        tmdb_id: series.tmdb_id, 
        title: series.title, 
        poster_path: series.poster_path,
        first_air_date: series.first_air_date, 
        genres: details.genres || [], 
        vote_average: series.vote_average,
        media_type: itemMediaType,
        position: position // 'top' or 'bottom'
      };
      
      const response = await axios.post(`${API}/watchlists/${id}/series`, newSeriesData, { withCredentials: true });
      
      setWatchlist(response.data);
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      
      const positionText = position === 'top' ? 'at #1' : 'at the bottom';
      toast.success(`Added "${series.title}" ${positionText}!`);
    } catch (error) {
      toast.error(error.response?.status === 400 ? 'Already in list' : 'Failed to add');
    }
  };
  
  const handleSaveNewSeriesOrder = async (orderedIds) => {
    try {
      // For joint lists, use my-ranking endpoint to update personal ranking
      if (isJointList) {
        const response = await axios.put(`${API}/watchlists/${id}/my-ranking`, { 
          series_order: orderedIds 
        }, { withCredentials: true });
        setWatchlist(response.data);
      } else {
        const response = await axios.put(`${API}/watchlists/${id}/reorder`, { 
          series_order: orderedIds 
        }, { withCredentials: true });
        setWatchlist(response.data);
      }
      setIsRankNewOpen(false);
      setNewlyAddedSeries(null);
      toast.success(`"${newlyAddedSeries?.title}" added and ranked!`);
    } catch (error) {
      toast.error('Failed to save order');
    }
  };
  
  const handleRemoveSeries = async (tmdbId) => {
    if (!window.confirm('Remove this show?')) return;
    try {
      const response = await axios.delete(`${API}/watchlists/${id}/series/${tmdbId}`, { withCredentials: true });
      setWatchlist(response.data);
      toast.success('Removed');
    } catch (error) {
      toast.error('Failed to remove');
    }
  };
  
  const handleViewDetails = async (series) => {
    setSelectedSeries(series);
    setLoadingDetails(true);
    try {
      const tmdbId = series.tmdb_id || series.id;
      const itemMediaType = series.media_type || 'tv';
      
      // Use canonical media API for details
      const details = await getTMDBDetails(tmdbId, itemMediaType);
      setSeriesDetails(details);
    } catch (error) {
      console.error('Canonical details failed, falling back:', error);
      // Fallback to old API
      try {
        const tmdbId = series.tmdb_id || series.id;
        const itemMediaType = series.media_type || 'tv';
        const endpoint = itemMediaType === 'movie' ? `/tmdb/movie/${tmdbId}` : `/tmdb/tv/${tmdbId}`;
        const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
        setSeriesDetails(response.data);
      } catch (e) {
        toast.error('Failed to load details');
      }
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const handleViewDetailsFromSearch = async (series) => {
    // View details without closing search dialog
    setSelectedSeries(series);
    setLoadingDetails(true);
    try {
      const itemMediaType = series.media_type || mediaType;
      
      // Use canonical media API for details
      const details = await getTMDBDetails(series.tmdb_id, itemMediaType);
      setSeriesDetails(details);
    } catch (error) {
      console.error('Canonical details failed, falling back:', error);
      // Fallback to old API
      try {
        const itemMediaType = series.media_type || mediaType;
        const endpoint = itemMediaType === 'movie' ? `/tmdb/movie/${series.tmdb_id}` : `/tmdb/tv/${series.tmdb_id}`;
        const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
        setSeriesDetails(response.data);
      } catch (e) {
        toast.error('Failed to load details');
      }
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const moveSeriesUp = async (index) => {
    if (index === 0) return;
    const newOrder = [...watchlist.series];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    await reorderSeries(newOrder);
  };
  
  const moveSeriesDown = async (index) => {
    if (index === watchlist.series.length - 1) return;
    const newOrder = [...watchlist.series];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    await reorderSeries(newOrder);
  };
  
  const reorderSeries = async (newOrder) => {
    try {
      // For joint lists, use my-ranking endpoint to update personal ranking
      if (isJointList) {
        const response = await axios.put(`${API}/watchlists/${id}/my-ranking`, { 
          series_order: newOrder.map(s => s.tmdb_id) 
        }, { withCredentials: true });
        setWatchlist(response.data);
      } else {
        const response = await axios.put(`${API}/watchlists/${id}/reorder`, { 
          series_order: newOrder.map(s => s.tmdb_id) 
        }, { withCredentials: true });
        setWatchlist(response.data);
      }
    } catch (error) {
      toast.error('Reorder failed');
    }
  };
  
  const handleDragReorder = async (orderedIds) => {
    try {
      // For joint lists, use my-ranking endpoint to update personal ranking
      if (isJointList) {
        const response = await axios.put(`${API}/watchlists/${id}/my-ranking`, { 
          series_order: orderedIds 
        }, { withCredentials: true });
        setWatchlist(response.data);
      } else {
        const response = await axios.put(`${API}/watchlists/${id}/reorder`, { 
          series_order: orderedIds 
        }, { withCredentials: true });
        setWatchlist(response.data);
      }
    } catch (error) {
      toast.error('Reorder failed');
    }
  };
  
  const handleMarkWatched = (series) => {
    setMarkWatchedSeries(series);
    setSelectedGrade(null);
    setRankingStep(false);
    setNewRankings([]);
    setIsFirstWatch(true);
    setIsBigScreen(false);
    setWatchComment('');
    setWatchDate(new Date().toISOString().split('T')[0]);
    setPrimaryGenre(series.genres?.[0] || null);
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
    setIsMarkWatchedOpen(true);
  };
  
  // Called when user clicks "Continue to Ranking" after selecting grade/comment/firstWatch
  const handleProceedToRanking = async (grade) => {
    setSelectedGrade(grade);
    setSavingWatched(true);
    
    try {
      // First, get existing watched items with the same grade for comparison
      const mediaType = watchlist?.media_category || 'tv';
      const response = await axios.get(`${API}/watched?grade=${grade}&media_type=${mediaType}`, { withCredentials: true });
      const items = response.data.series || [];  // Backend returns "series" not "watched"
      setExistingItems(items);
      
      if (items.length === 0) {
        // No existing items with this grade, save directly at rank 1
        await saveWatched(grade, 1);
      } else {
        // Start 1v1 comparison using binary search
        setSearchLow(0);
        setSearchHigh(items.length);
        const midIndex = Math.floor(items.length / 2);
        setComparisonItem(items[midIndex]);
        setComparisonStep(true);
        setSavingWatched(false);
      }
    } catch (error) {
      toast.error('Failed to load comparison items');
      setSavingWatched(false);
    }
  };
  
  // Handle 1v1 comparison choice
  const handleComparison = async (preferNew) => {
    let newLow = searchLow;
    let newHigh = searchHigh;
    
    const midIndex = Math.floor((newLow + newHigh) / 2);
    
    if (preferNew) {
      // User prefers new item over comparison item, search in higher ranks (lower indices)
      newHigh = midIndex;
    } else {
      // User prefers comparison item, search in lower ranks (higher indices)
      newLow = midIndex + 1;
    }
    
    if (newLow >= newHigh) {
      // Found the right position, save with determined rank
      await saveWatched(selectedGrade, newLow + 1);
    } else {
      // Continue binary search
      setSearchLow(newLow);
      setSearchHigh(newHigh);
      const nextMidIndex = Math.floor((newLow + newHigh) / 2);
      setComparisonItem(existingItems[nextMidIndex]);
    }
  };
  
  // Save the watched item with determined rank
  const saveWatched = async (grade, rank) => {
    setSavingWatched(true);
    const mediaType = watchlist?.media_category || 'tv';
    
    try {
      // Add to watched
      await axios.post(`${API}/watched/direct`, {
        tmdb_id: markWatchedSeries.tmdb_id,
        title: markWatchedSeries.title,
        poster_path: markWatchedSeries.poster_path,
        media_type: mediaType,
        grade: grade,
        rank_in_group: rank,
        is_first_watch: isFirstWatch,
        is_big_screen: mediaType === 'movie' ? isBigScreen : false,
        watch_date: watchDate,
        comment: watchComment || null,
        genres: markWatchedSeries.genres || [],
        primary_genre: primaryGenre
      }, { withCredentials: true });
      
      // Also remove from the watchlist permanently via API
      try {
        await axios.delete(`${API}/watchlists/${id}/series/${markWatchedSeries.tmdb_id}`, { withCredentials: true });
      } catch (removeError) {
        console.warn('Failed to remove item from watchlist (may already be removed):', removeError.message);
      }
      
      toast.success(`Marked "${markWatchedSeries.title}" as watched!`);
      setIsMarkWatchedOpen(false);
      
      // Update local state to reflect removal
      setWatchlist(prev => ({ 
        ...prev, 
        series: prev.series.filter(s => s.tmdb_id !== markWatchedSeries.tmdb_id) 
      }));
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingWatched(false);
    }
  };
  
  // Legacy handler - now mostly unused but kept for compatibility
  const handleGradeSelect = async (grade) => {
    // Redirect to the new flow
    handleProceedToRanking(grade);
  };
  
  const moveRankUp = (index) => {
    if (index === 0) return;
    const updated = [...newRankings];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updated.forEach((r, i) => r.rank = i + 1);
    setNewRankings(updated);
  };
  
  const moveRankDown = (index) => {
    if (index === newRankings.length - 1) return;
    const updated = [...newRankings];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updated.forEach((r, i) => r.rank = i + 1);
    setNewRankings(updated);
  };
  
  const handleSaveRankings = async () => {
    try {
      await axios.put(`${API}/watched/${newRankings[0].id}/rank`, { 
        rankings: newRankings.map(r => ({ id: r.id, rank: r.rank })) 
      }, { withCredentials: true });
      toast.success('Rankings saved!');
      setIsMarkWatchedOpen(false);
    } catch (error) {
      toast.error('Save failed');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Determine back navigation based on media category
  const backPath = watchlist?.media_category === 'movie' ? '/films' : '/tv-shows';
  
  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header with full navigation */}
      <AppHeader user={user} showBack={true} backPath={backPath} />
      
      {/* Sub-header with list actions */}
      <div className="bg-[#F87171]/90 border-t border-white/10">
        <div className="container mx-auto px-6 py-3 flex items-center justify-end gap-2">
          {isJointList && watchlist?.share_code && (
            <>
              <Button 
                data-testid="invite-friends-btn"
                variant="outline" 
                onClick={handleOpenInviteFriends}
                className="rounded-full border-white/30 text-white hover:bg-white/10 bg-white/10"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite
              </Button>
              <Button 
                data-testid="share-link-btn"
                variant="outline" 
                onClick={handleCopyShareLink}
                className="rounded-full border-white/30 text-white hover:bg-white/10 bg-white/10"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Link'}
              </Button>
            </>
          )}
          <Button data-testid="add-series-btn" onClick={() => setIsSearchOpen(true)} className="bg-white text-[#F87171] hover:bg-white/90 rounded-full">
            <Plus className="w-4 h-4 mr-2" />Add
          </Button>
        </div>
      </div>
      
      <main className="container mx-auto px-6 py-8">
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  {isEditingTitle ? (
                    <div className="flex items-center gap-2 flex-1 max-w-md">
                      <Input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveTitle();
                          if (e.key === 'Escape') handleCancelEditTitle();
                        }}
                        className="text-xl font-bold"
                        autoFocus
                        data-testid="edit-title-input"
                      />
                      <Button size="sm" onClick={handleSaveTitle} className="bg-[#F87171] hover:bg-[#EF4444] rounded-full" data-testid="save-title-btn">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelEditTitle} className="rounded-full" data-testid="cancel-edit-btn">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3D4F6F]">{watchlist.title}</h1>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={handleStartEditTitle}
                        className="rounded-full opacity-60 hover:opacity-100"
                        data-testid="edit-title-btn"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {isJointList && !isEditingTitle && (
                    <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {memberCount} {memberCount === 1 ? 'member' : 'members'}
                    </span>
                  )}
                </div>
                {watchlist.description && <p className="text-[#64748B] mt-1">{watchlist.description}</p>}
                <p className="text-sm text-[#94A3B8] mt-2">
                  {watchlist.series?.length || 0} shows • Drag to reorder
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* New Joiner Prompt */}
        {currentUserHasEmptyList && (
          <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-purple-500 to-pink-500" data-testid="welcome-prompt">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 text-white">
                  <h3 className="font-outfit font-semibold text-lg" data-testid="welcome-title">Welcome to the list!</h3>
                  <p className="text-white/90 text-sm">
                    Start adding shows to build your personal ranking. Your picks will be combined with others to find common favorites.
                  </p>
                </div>
                <Button 
                  onClick={() => setIsSearchOpen(true)}
                  className="bg-white text-purple-600 hover:bg-white/90 rounded-full"
                  data-testid="start-adding-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Start Adding
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Ranking View Toggle for Joint Lists */}
        {isJointList && (watchlist.series?.length > 0 || watchlist.combined_view) && (
          <Card className="mb-6 border-0 shadow-lg bg-purple-50/95 backdrop-blur-sm" data-testid="ranking-view-toggle">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">View Rankings</span>
              </div>
              <Tabs value={rankingView} onValueChange={setRankingView}>
                <TabsList className="bg-white/50 flex-wrap h-auto gap-1 p-1">
                  <TabsTrigger 
                    value="combined" 
                    className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-full text-xs px-3"
                    data-testid="ranking-view-combined"
                  >
                    Combined
                  </TabsTrigger>
                  {allMembers.map(member => (
                    <TabsTrigger 
                      key={member.user_id}
                      value={member.user_id}
                      className="data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-full text-xs px-3"
                      data-testid={`ranking-view-${member.user_id}`}
                    >
                      {member.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <p className="text-xs text-purple-600 mt-2">
                {rankingView === 'combined' 
                  ? 'Common shows ranked by popularity, plus unique picks from each member'
                  : `Showing ${allMembers.find(m => m.user_id === rankingView)?.name || 'user'}'s personal ranking`}
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Content based on view */}
        {!isJointList || rankingView !== 'combined' ? (
          // Personal list OR individual member view
          watchlist.series?.length === 0 ? (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-[#F87171]/10 rounded-full flex items-center justify-center mb-4">
                  <Tv className="w-8 h-8 text-[#F87171]" />
                </div>
                <h3 className="font-outfit font-semibold text-[#3D4F6F] text-lg mb-2">No shows yet</h3>
                <p className="text-[#64748B] text-center mb-6 max-w-sm">Search and add shows to get started.</p>
                <Button data-testid="add-first-show-btn" onClick={() => setIsSearchOpen(true)} className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" />Add Your First Show
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardContent className="p-4">
                <SortableSeriesList
                  series={displayedSeries}
                  onReorder={handleDragReorder}
                  onViewDetails={handleViewDetails}
                  onRemove={handleRemoveSeries}
                  onMarkWatched={handleMarkWatched}
                  disabled={isJointList && rankingView !== 'combined' && rankingView !== user?.user_id}
                />
              </CardContent>
            </Card>
          )
        ) : (
          // Joint list combined view
          watchlist.combined_view ? (
            <JointListCombinedView
              combinedView={watchlist.combined_view}
              onViewDetails={handleViewDetails}
              onRemove={handleRemoveSeries}
              onMarkWatched={handleMarkWatched}
              allMembers={allMembers}
            />
          ) : watchlist.series?.length === 0 ? (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="font-outfit font-semibold text-[#3D4F6F] text-lg mb-2">No shows added yet</h3>
                <p className="text-[#64748B] text-center mb-6 max-w-sm">
                  Members need to add shows to their lists. Common picks will appear here!
                </p>
                <Button data-testid="add-show-btn" onClick={() => setIsSearchOpen(true)} className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" />Add Shows
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardContent className="p-4">
                <SortableSeriesList
                  series={displayedSeries}
                  onReorder={handleDragReorder}
                  onViewDetails={handleViewDetails}
                  onRemove={handleRemoveSeries}
                  onMarkWatched={handleMarkWatched}
                  disabled={true}
                />
              </CardContent>
            </Card>
          )
        )}
      </main>
      
      <SearchSeriesDialog
        open={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        searching={searching}
        searchResults={searchResults}
        onAddSeries={handleAddSeries}
        onViewDetails={handleViewDetailsFromSearch}
        mediaType={mediaType}
        setMediaType={setMediaType}
        onQuickAdd={handleQuickAdd}
        lockedMediaType={watchlist?.media_category === 'movie' ? 'movie' : watchlist?.media_category === 'tv' ? 'tv' : null}
      />
      
      <SeriesDetailsDialog
        open={!!selectedSeries}
        onClose={() => { setSelectedSeries(null); setSeriesDetails(null); }}
        loading={loadingDetails}
        details={seriesDetails}
      />
      
      <MarkWatchedDialog
        open={isMarkWatchedOpen}
        onClose={() => setIsMarkWatchedOpen(false)}
        series={markWatchedSeries}
        selectedGrade={selectedGrade}
        onGradeSelect={handleGradeSelect}
        rankingStep={rankingStep}
        newRankings={newRankings}
        mediaType={markWatchedSeries?.media_type || watchlist?.media_category || 'tv'}
        onMoveRankUp={moveRankUp}
        onMoveRankDown={moveRankDown}
        onSaveRankings={handleSaveRankings}
        isFirstWatch={isFirstWatch}
        setIsFirstWatch={setIsFirstWatch}
        isBigScreen={isBigScreen}
        setIsBigScreen={setIsBigScreen}
        comment={watchComment}
        setComment={setWatchComment}
        watchDate={watchDate}
        setWatchDate={setWatchDate}
        genres={markWatchedSeries?.genres || []}
        primaryGenre={primaryGenre}
        setPrimaryGenre={setPrimaryGenre}
        onProceedToRanking={handleProceedToRanking}
        comparisonStep={comparisonStep}
        comparisonItem={comparisonItem}
        onComparison={handleComparison}
        savingWatched={savingWatched}
        existingItemsCount={existingItems.length}
      />
      
      <RankNewSeriesDialog
        open={isRankNewOpen}
        onClose={() => {
          setIsRankNewOpen(false);
          setNewlyAddedSeries(null);
          toast.success(`"${newlyAddedSeries?.title}" added!`);
        }}
        newSeries={newlyAddedSeries}
        allSeries={isJointList ? getUserPersonalSeries() : (watchlist?.series || [])}
        onSaveOrder={handleSaveNewSeriesOrder}
      />
      
      {/* Invite Friends Dialog */}
      <Dialog open={showInviteFriends} onOpenChange={setShowInviteFriends}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3D4F6F] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#F87171]" />
              Invite Friends
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Send an invitation to your friends to join this list. They'll receive it in their messages.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingFriends ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#F87171]" />
              </div>
            ) : friends.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#64748B] text-sm">
                  {watchlist?.shared_with?.length > 0 
                    ? "All your friends are already members of this list!"
                    : "No friends to invite yet. Follow someone and get followed back to start sharing lists!"
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {friends.map((friend) => (
                  <div
                    key={friend.user_id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {friend.avatar_url ? (
                          <AvatarImage src={friend.avatar_url} />
                        ) : (
                          <AvatarFallback className="bg-[#F87171]/10 text-[#F87171]">
                            {friend.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        )}
                      </Avatar>
                      <span className="font-medium text-[#3D4F6F]">{friend.name}</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleInviteFriend(friend.user_id)}
                      disabled={invitingFriend === friend.user_id}
                      className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                    >
                      {invitingFriend === friend.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-1" />
                          Invite
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
